"use client";

import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import {
  GuideTabContent,
  GuideSectionType,
  GuideContentBlocksSection as IGuideContentBlocksSection,
  GuideRecommendationCardGridSection as IGuideRecommendationCardGridSection,
  GuideServicesCarouselSection as IGuideServicesCarouselSection,
} from "@/common/lib/types";
import { SingleFaqSection } from "./FaqTabVariant";
import PortableText from "@/components/common/PortableText";
import { CLink } from "@/components/common/customLink/CustomLink";
import Image from "@/components/ui/imageV2/ImageV2";
import LazyLoadedVideo from "@/components/common/LazyLoadedVideo";

interface GuideTabVariantProps {
  data: GuideTabContent;
}

const GuideContentBlocksSection: React.FC<{ data: IGuideContentBlocksSection }> = ({ data }) => {
  if (!data || data.enable === false) return null;
  const bgColor = data?.backgroundColor || "#FFFFFF";

  return (
    <section className="mb-10 bg-white w-full">
      {data.blocks?.length ? (
        <div className="w-full">
          {data.blocks
            .filter((block: any) => block.enable !== false)
            .map((block: any, index: number) => {
              const isReversed =
                block.mediaPosition === "right" ||
                (block.mediaPosition == null && index % 2 !== 0);
              const stepNumber = String(index + 1).padStart(2, "0");
              const imageSrc = block.desktopImage || block.mobileImage;

              return (
                <div
                  key={block.key || index}
                  className="grid grid-cols-1 lg:grid-cols-2 w-full lg:items-stretch"
                >
                  {/* Media side */}
                  <div
                    className={`relative w-full aspect-[4/3] lg:aspect-auto lg:min-h-[560px] lg:h-full ${
                      isReversed ? "lg:order-2" : "lg:order-1"
                    }`}
                  >
                    {block.mediaType === "video" && block.desktopVideo && (
                      <LazyLoadedVideo
                        src={block.desktopVideo}
                        className="w-full h-full object-cover"
                        autoPlay
                        muted
                        loop
                        playsInline
                      />
                    )}
                    {(block.mediaType === "image" || !block.mediaType) &&
                      imageSrc && (
                        <Image
                          src={imageSrc}
                          alt={block.title || ""}
                          fill
                          className="object-cover"
                        />
                      )}
                  </div>

                  {/* Content side */}
                  <div
                    className={`flex items-center ${
                      isReversed ? "lg:order-1" : "lg:order-2"
                    } w-full lg:min-h-[560px] lg:h-full`}
                    style={{ backgroundColor: bgColor }}
                  >
                    <div className="px-6 md:px-12 lg:px-20 py-8 md:py-14 lg:py-16 max-w-[650px] mx-auto lg:mx-0">
                      <div className="mb-3 md:mb-4">
                        <h3 className="font-semibold text-black text-[22px] md:text-[32px] leading-[1.2]">
                          {block.title}
                        </h3>
                      </div>

                      <div className="leading-relaxed mb-4 md:mb-6 text-tertiary-6 text-[14px] md:text-[18px] font-normal">
                        <PortableText value={block.description} />
                      </div>

                      {block.ctaText && (
                        <CLink
                          href={block.ctaLink?.url || "#"}
                          className="flex items-center justify-center bg-black text-white transition hover:bg-opacity-80 w-[218px] h-[36px] gap-[10px] py-[9px] px-8 text-sm font-bold whitespace-nowrap"
                        >
                          {block.ctaText}
                        </CLink>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      ) : null}
    </section>
  );
};

const RecommendationCardGrid: React.FC<{ data: IGuideRecommendationCardGridSection }> = ({ data }) => {
  if (!data || data.enable === false) return null;
  const cards = data.cards?.filter((c: any) => c.enable !== false) || [];
  const bgColor = data.backgroundColor || "#FFFFFF";
  const cardBg = data.cardBackgroundColor || "#EAF2E8";
  const badgeBg = data.badgeBackgroundColor || "#DDEBDC";

  return (
    <section className="w-full py-[30px] md:py-[40px]" style={{ backgroundColor: bgColor }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {(data.title || data.description) && (
          <div className="text-center mb-8 md:mb-10">
            {data.title && (
              <h2 className="text-xl md:text-2xl font-bold text-black mb-3">{data.title}</h2>
            )}
            {data.description && (
              <div className="text-sm md:text-base text-tertiary-6 max-w-2xl mx-auto">
                <PortableText value={data.description} />
              </div>
            )}
          </div>
        )}

        {cards.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {cards.map((card: any, idx: number) => (
              <div
                key={card.key || idx}
                className="overflow-hidden flex flex-col h-full border-0"
                style={{ backgroundColor: cardBg }}
              >
                {card.badge && (
                  <div
                    className="w-full py-2 px-3 text-xs font-semibold text-[#2D3B2D] text-center"
                    style={{ backgroundColor: badgeBg }}
                  >
                    {card.badge}
                  </div>
                )}
                <div className="p-6 md:p-7 flex flex-col items-center text-center flex-grow">
                  {card.icon ? (
                    <div className="w-11 h-11 relative mb-5 flex items-center justify-center">
                      <Image
                        src={card.icon}
                        alt={card.title || "icon"}
                        width={44}
                        height={44}
                        className="object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-11 h-11 mb-5 flex items-center justify-center text-black">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  )}
                  {card.title && (
                    <h3 className="text-[14px] md:text-[18px] font-medium text-black mb-3">{card.title}</h3>
                  )}
                  {card.description && (
                    <div className="text-xs md:text-sm text-tertiary-6 leading-relaxed text-center">
                      <PortableText value={card.description} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const ServicesCarouselSection: React.FC<{ data: IGuideServicesCarouselSection }> = ({ data }) => {
  if (!data || data.enable === false) return null;
  const services = data.services?.filter((s: any) => s.enable !== false) || [];
  const bgColor = data.backgroundColor || "#F8F8F8";
  const cardBg = data.cardBackgroundColor || "#FFFFFF";
  const btnBg = data.buttonBackgroundColor || "#000000";
  const btnColor = data.buttonTextColor || "#FFFFFF";

  const [isRtl, setIsRtl] = useState(false);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: isRtl ? "end" : "start",
    direction: isRtl ? "rtl" : "ltr",
    slidesToScroll: 1,
    containScroll: "trimSnaps",
  });

  useEffect(() => {
    if (typeof document !== "undefined") {
      const rtl = document.documentElement.dir === "rtl";
      setIsRtl(rtl);
      if (emblaApi) {
        emblaApi.reInit({
          align: rtl ? "end" : "start",
          direction: rtl ? "rtl" : "ltr",
        });
      }
    }
  }, [emblaApi]);

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  return (
    <div className="w-full py-[30px] md:py-[40px] bg-white">
      <section className="w-full py-[40px] md:py-[44px]" style={{ backgroundColor: bgColor }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {(data.title || data.description) && (
            <div className="text-center mb-8 md:mb-12">
              {data.title && (
                <h2 className="text-[18px] md:text-[24px] font-semibold text-black mb-3">{data.title}</h2>
              )}
              {data.description && (
                <div className="text-sm md:text-base text-tertiary-6 mx-auto">
                  <PortableText value={data.description} />
                </div>
              )}
            </div>
          )}

          {services.length > 0 && (
            <>
              {/* Mobile View (< 768px): Vertical Stacked Cards */}
              <div className="flex flex-col gap-6 md:hidden">
                {services.map((service: any, idx: number) => (
                  <div
                    key={service.key || idx}
                    className="w-full flex flex-col justify-between border border-gray-100 h-auto"
                    style={{ backgroundColor: cardBg }}
                  >
                    <div>
                      {(service.desktopImage || service.mobileImage) && (
                        <div className="relative w-full h-56 bg-gray-100">
                          <Image
                            src={service.desktopImage || service.mobileImage}
                            alt={service.title || "service"}
                            fill
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="p-4">
                        {service.title && (
                          <h3 className="text-base font-semibold text-black mb-1">{service.title}</h3>
                        )}
                        {service.sublabel && (
                          <p className="text-xs text-gray-400 font-medium mb-3 pb-3 border-b border-gray-100">
                            {service.sublabel}
                          </p>
                        )}
                        {service.description && (
                          <div className="text-xs text-tertiary-6 leading-relaxed">
                            <PortableText value={service.description} />
                          </div>
                        )}
                      </div>
                    </div>

                    {service.ctaText && (
                      <div className="p-4 pt-0">
                        <CLink
                          href={service.ctaLink?.url || "#"}
                          className="inline-block py-3.5 px-4 text-xs font-bold text-center tracking-wider transition-opacity hover:opacity-90 rounded-none"
                          style={{ backgroundColor: btnBg, color: btnColor }}
                        >
                          {service.ctaText}
                        </CLink>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop View (>= 768px): Carousel */}
              <div className="relative px-0 hidden md:block">
                {/* Left Side Navigation Button */}
                <button
                  type="button"
                  onClick={scrollPrev}
                  disabled={!canScrollPrev}
                  className="absolute -left-4 md:-left-12 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 hidden md:flex items-center justify-center text-black transition-all disabled:opacity-30 disabled:pointer-events-none"
                  aria-label="Previous slide"
                >
                  <svg className="w-4 h-4 stroke-current stroke-[2.5]" viewBox="0 0 24 24" fill="none">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <div className="overflow-hidden" ref={emblaRef}>
                  <div className="flex gap-6">
                    {services.map((service: any, idx: number) => (
                      <div
                        key={service.key || idx}
                        className="flex-[0_0_100%] sm:flex-[0_0_calc((100%-1.5rem)/2)] lg:flex-[0_0_calc((100%-3rem)/3)] min-w-0 flex flex-col justify-between border border-gray-100 h-auto"
                        style={{ backgroundColor: cardBg }}
                      >
                        <div>
                          {(service.desktopImage || service.mobileImage) && (
                            <div className="relative w-full h-56 md:h-64 bg-gray-100">
                              <Image
                                src={service.desktopImage || service.mobileImage}
                                alt={service.title || "service"}
                                fill
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <div className="p-4 md:p-8">
                            {service.title && (
                              <h3 className="text-base md:text-lg font-semibold text-black mb-1">{service.title}</h3>
                            )}
                            {service.sublabel && (
                              <p className="text-xs text-gray-400 font-medium mb-3 pb-3 border-b border-gray-100">
                                {service.sublabel}
                              </p>
                            )}
                            {service.description && (
                              <div className="text-xs md:text-sm text-tertiary-6 leading-relaxed">
                                <PortableText value={service.description} />
                              </div>
                            )}
                          </div>
                        </div>

                        {service.ctaText && (
                          <div className="p-6 pt-0">
                            <CLink
                              href={service.ctaLink?.url || "#"}
                              className="inline-block py-3.5 px-4 text-xs font-bold text-center tracking-wider transition-opacity hover:opacity-90 rounded-none"
                              style={{ backgroundColor: btnBg, color: btnColor }}
                            >
                              {service.ctaText}
                            </CLink>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right Side Navigation Button */}
                <button
                  type="button"
                  onClick={scrollNext}
                  disabled={!canScrollNext}
                  className="absolute -right-4 md:-right-12 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-10 md:h-10 hidden md:flex items-center justify-center text-black transition-all disabled:opacity-30 disabled:pointer-events-none"
                  aria-label="Next slide"
                >
                  <svg className="w-4 h-4 stroke-current stroke-[2.5]" viewBox="0 0 24 24" fill="none">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};


const GuideTabVariant: React.FC<GuideTabVariantProps> = ({ data }) => {
  if (!data?.sections?.length) return null;

  return (
    <div className="w-full flex flex-col bg-white">
      {data.sections
        .filter((sec: any) => sec.enable !== false)
        .map((sec: GuideSectionType, idx: number) => {
          switch (sec.type) {
            case "guideContentBlocksSection":
              return <GuideContentBlocksSection key={sec.key || idx} data={sec} />;
            case "guideRecommendationCardGridSection":
              return <RecommendationCardGrid key={sec.key || idx} data={sec} />;
            case "guideServicesCarouselSection":
              return <ServicesCarouselSection key={sec.key || idx} data={sec} />;
            case "guideTabFaqSection":
              return <SingleFaqSection key={sec.key || idx} data={sec as any} />;
            default:
              return null;
          }
        })}
    </div>
  );
};

export default GuideTabVariant;
